import { inject, Injectable } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { untilDestroyed } from '@ngneat/until-destroy';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import isEqual from 'fast-deep-equal';
import { BehaviorSubject, concatMap, finalize, map, Observable, of, switchMap, take } from 'rxjs';

import { fromTargetWorkflow } from '@blaze-dashboard/editor-data-access';
import {
  ColumnId,
  ComparisonOperator,
  LogicalType,
  QueryNodeType,
  ReferenceInfo,
  TableInfo,
  TagItemInfo,
} from '@blaze-shared/api-contracts-domain';
import {
  DatasetsApiService,
  TablesApiService,
  TablesCollectionService,
} from '@blaze-shared/datasets-data-access';
import {
  BlazeTable,
  convertBlazeTableSchemaToBlazeTable,
  IS_NEW_ROW_COL,
  ROW_ID_COL,
} from '@blaze-shared/datasets-domain';
import { compare } from '@blaze-shared/platform-util';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BlazeTableSchema,
  ColumnType,
  DatasetColumn,
  DatasetLinkValue,
  DatasetRow,
  DEFAULT_ID_COL,
  findColumnIndex,
  getDisplayStringFromLinkValue,
  isDatasetLinkValue,
  Signature,
} from '@blaze-shared/schema-domain';
import { DialogService } from '@blaze-shared/shared-feature';
import { deepClone, deleteItemFromArray, GeoPoint } from '@blaze-shared/shared-util';

import { BlazeTableLoggerService } from './blaze-table-logger.service';
import { BlazeTableRecord, BlazeTableStore } from './blaze-table.store';

const NOT_ALLOWED_CHARS = ['"', '(', ')', '[', ']', '{', '}', '$', '.', '|'];

export type BlazeTableValue =
  | null
  | string
  | boolean
  | number
  | DatasetLinkValue
  | string[]
  | DatasetLinkValue[]
  | Signature
  | GeoPoint;

@Injectable({ providedIn: 'root' })
export class BlazeTableService {
  private readonly blazeTableStore = inject(BlazeTableStore);
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly dialogService = inject(DialogService);
  private readonly logger = inject(BlazeTableLoggerService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);
  private readonly tableCollService = inject(TablesCollectionService);
  private readonly tablesApiService = inject(TablesApiService);

  private readonly config$$ = new BehaviorSubject<BlazeTable | undefined>(undefined);
  readonly config$ = this.config$$.asObservable();
  get config(): BlazeTable {
    const value = this.config$$.value;
    if (!value) {
      this.logger.error('Missing config$$ but already asked');
      throw new Error('[Blaze Table] Config is not initialized yet');
    }
    return value;
  }

  addRowId(row: DatasetRow): DatasetRow {
    if (!row[ROW_ID_COL]) {
      row[ROW_ID_COL] = crypto.randomUUID();
    }
    return row;
  }

  createColumn(column: DatasetColumn, columns: DatasetColumn[]): DatasetColumn {
    return {
      ...column,
      name: this.normalizeColumnName(column.name, columns),
    };
  }

  createRow(table: BlazeTable): DatasetRow {
    let row: DatasetRow = {};
    const columns = this.prepareColumns(table);
    columns.forEach((col) => {
      row[col.name] = null;
    });
    row = this.addRowId(row);
    row[IS_NEW_ROW_COL] = true;
    return row;
  }

  /**
   * Deletes specified rows from the list of all rows.
   */
  deleteRows(rows: DatasetRow[], rowsToDelete: { [key: string]: DatasetRow }): DatasetRow[] {
    const updatedRows: DatasetRow[] = [];
    rows.forEach((row) => {
      if (!rowsToDelete[row[ROW_ID_COL]]) {
        updatedRows.push({ ...row });
      }
    });
    return updatedRows;
  }

  duplicateRowData(
    sourceColumnName: string,
    targetColumnName: string,
    rows: DatasetRow[],
  ): DatasetRow[] {
    return rows.map<DatasetRow>((row) => ({
      ...row,
      [targetColumnName]: row[sourceColumnName],
    }));
  }

  validateColumnName(
    name: string,
    sourceColumn: DatasetColumn | null,
    columns: DatasetColumn[],
  ):
    | { isValid: true }
    | {
        isValid: false;
        error: {
          code: 'EMPTY' | 'NOT_ALLOWED_CHAR' | 'NOT_UNIQUE';
          message: string;
        };
      } {
    if (!name || !name.trim()) {
      return {
        isValid: false,
        error: {
          code: 'EMPTY',
          message: "Column name can't be empty",
        },
      };
    }

    for (const notAllowedChar of NOT_ALLOWED_CHARS) {
      if (name.includes(notAllowedChar)) {
        return {
          isValid: false,
          error: {
            code: 'NOT_ALLOWED_CHAR',
            message: `Column name "${name}" contains not allowed char ${notAllowedChar}`,
          },
        };
      }
    }

    const colIndex = columns.findIndex((col) => col.name.toLowerCase() === name.toLowerCase());
    const sourceColIndex = columns.findIndex((col) => col.name === sourceColumn?.name);

    if (colIndex !== sourceColIndex && colIndex > -1) {
      return {
        isValid: false,
        error: {
          code: 'NOT_UNIQUE',
          message: `Column with name "${name}" already exists`,
        },
      };
    }

    return { isValid: true };
  }

  /**
   * Generates a duplicate name of the column while preserving the uniquness of the name.
   */
  normalizeColumnName(colName: string, columns: DatasetColumn[]): string {
    colName = colName.trim();
    const hasColumnWithSameName = !!columns.find((col) => col.name === colName);
    if (!hasColumnWithSameName) {
      return colName;
    }
    const colsWithName = columns.filter((col) => col.name.startsWith(`${colName}-`));
    let maxNum = 1;
    colsWithName.forEach((col) => {
      const colParts = col.name.split('-');
      const colSuffix = parseInt(colParts[colParts.length - 1], 10);
      maxNum = !Number.isNaN(colSuffix) && colSuffix >= maxNum ? colSuffix + 1 : maxNum;
    });
    return `${colName}-${maxNum}`;
  }

  getColumnTypeIcon(
    column: Pick<DatasetColumn, 'columnType' | 'format' | 'isSecure' | 'isAccessRole'>,
    isInline?: boolean,
  ): string {
    switch (column.columnType) {
      case 'Boolean':
        return 'icon_field_type_bool_16';
      case 'DateTime':
      case 'Time':
        return 'icon_field_input_datetime_24';
      case 'Date':
      case 'CreatedTime':
      case 'LastUpdatedTime':
        return 'icon_field_type_date_16';
      case 'Duration':
        return 'icon_field_type_duration_16';
      case 'File':
        return 'icon_field_type_file_16';
      case 'Formula':
        if (isInline) {
          return 'icon_field_type_extra_col_16';
        }
        return 'icon_field_type_formula_16';
      case 'Image':
        return 'icon_field_type_image_16';
      case 'Link':
        return 'icon_field_type_link_16';
      case 'Rollup':
        return 'icon_field_type_rollup_16';
      case 'AutoNumber':
      case 'Number':
        return 'icon_field_type_number_16';
      case 'CreatedBy':
      case 'UpdatedBy':
        return 'icon_field_type_text_16';
      case 'String':
        if (column.isSecure) {
          return 'icon_field_type_secure_text_16';
        }
        switch (column.format?.formatType) {
          case 'Email':
            return 'icon_field_type_email_16';
          case 'PhoneNumber':
            return 'icon_field_type_phone_16';
          case 'Url':
            return 'icon_field_type_url_16';
          case 'Duration':
            return 'icon_field_type_duration_16';
          default:
            return 'icon_field_type_text_16';
        }
      case 'Tag': {
        return column.isAccessRole ? 'icon_field_type_access_role_16' : 'icon_field_type_tag_16';
      }
      case 'Signature':
        return 'icon_field_type_signature_16';
      case 'GeoPoint':
        return 'icon_field_type_location_16';
      default:
        return '';
    }
  }

  getLinkDisplayValue(tableRecord: BlazeTableRecord): string {
    if (isDatasetLinkValue(tableRecord?.value)) {
      return (
        (isDatasetLinkValue(tableRecord.value.display) &&
          (getDisplayStringFromLinkValue(tableRecord.value.display.display) ||
            tableRecord.value.display.rowId)) ||
        getDisplayStringFromLinkValue(tableRecord.value.display) ||
        tableRecord.value.rowId
      );
    } else if (typeof tableRecord?.value === 'string' || typeof tableRecord?.value === 'number') {
      return tableRecord?.displayValue || (tableRecord?.value as string) || '';
    }
    return '';
  }

  isEmptyCell(row: DatasetRow): boolean {
    for (const key in row) {
      if (
        row[key] !== null &&
        key !== DEFAULT_ID_COL &&
        key !== ROW_ID_COL &&
        key !== IS_NEW_ROW_COL
      ) {
        return false;
      }
    }
    return true;
  }

  isNewRow(row: DatasetRow): boolean {
    return !!row?.[IS_NEW_ROW_COL];
  }

  sortRows(rows: DatasetRow[], column: DatasetColumn, ascending: boolean): DatasetRow[] {
    return rows.sort((rowA, rowB) => {
      return compare(rowA[column.name], rowB[column.name], ascending);
    });
  }

  hasExpandableLinks(tableId: string): boolean {
    return this.schemaService.hasExpandableLinks(tableId);
  }

  syncConfigWithSchema(schema: BlazeTableSchema): BlazeTable {
    const refreshed = convertBlazeTableSchemaToBlazeTable(schema);
    const current = this.config$$.value;
    if (!current || current.id !== refreshed.id) {
      return refreshed;
    }
    const displayColumn = refreshed.displayColumn || current.displayColumn;
    if (isEqual(current.columns, refreshed.columns) && current.displayColumn === displayColumn) {
      return current;
    }

    const config = deepClone(current);
    config.columns = [...refreshed.columns];
    config.displayColumn = displayColumn;
    this.updateConfig(config);
    return config;
  }

  updateConfig(config: BlazeTable | undefined): void {
    this.config$$.next(config);
  }

  /**
   * Adds new column to each row. This is necessary when a new column is added to the table.
   */
  updateTableRows(rows: (DatasetRow | undefined)[], column: DatasetColumn): DatasetRow[] {
    const updatedRows: DatasetRow[] = [];
    rows.forEach((row, index) => {
      if (!row) {
        return;
      }
      const updatedRow = { ...row };
      updatedRow[column.name] = null;
      updatedRows[index] = updatedRow;
    });
    return updatedRows;
  }

  /**
   * Adds new column to each row. This is necessary when a new column is added to the table.
   */
  updateTableRowsAfterDelete(
    rows: (DatasetRow | undefined)[],
    column: DatasetColumn,
  ): DatasetRow[] {
    const updatedRows: DatasetRow[] = [];
    rows.forEach((row, index) => {
      const updatedRow = { ...row };
      delete updatedRow[column.name];
      updatedRows[index] = updatedRow;
    });
    return updatedRows;
  }

  updateTableRowsAfterEdit(
    rows: (DatasetRow | undefined)[],
    oldColumn: DatasetColumn,
    newColumn: DatasetColumn,
  ): (DatasetRow | undefined)[] {
    if (oldColumn.name !== newColumn.name) {
      // Name has changed. Update name of the column only
      const updatedRows: DatasetRow[] = [];
      rows.forEach((row, index) => {
        if (!row) {
          return;
        }
        const updatedRow = { ...row };
        updatedRow[newColumn.name] = row[oldColumn.name];
        delete updatedRow[oldColumn.name];
        updatedRows[index] = updatedRow;
      });
      return updatedRows;
    }

    // Definition of tags changed remove non-existing values from rows
    if (newColumn.tagDefinition && !isEqual(oldColumn.tagDefinition, newColumn.tagDefinition)) {
      const updatedRows: DatasetRow[] = [];
      const modifications: Dictionary<{ from: TagItemInfo; to: TagItemInfo | null }> = {};

      oldColumn.tagDefinition?.items.forEach((oldDef) => {
        const newDef = newColumn.tagDefinition?.items.find((newDef) => newDef.id === oldDef.id);

        if (newDef && newDef.value !== oldDef.value) {
          modifications[oldDef.value] = { from: oldDef, to: newDef };
        }
        if (!newDef) {
          modifications[oldDef.value] = { from: oldDef, to: null };
        }
      });

      // If there are no modifications no rows needs to be updated
      if (!Object.values(modifications).length) {
        return rows;
      }

      rows.forEach((row, index) => {
        const updatedRow = { ...row };
        const currentValue = updatedRow[newColumn.name];
        if (Array.isArray(currentValue)) {
          updatedRow[newColumn.name] = currentValue
            // Remove tags with no new definition
            .filter((v) => {
              const modification = modifications[v];
              return !(modification && modification.to === null);
            })
            // Update tags value
            .map((v) => {
              const modification = modifications[v];
              return modification?.to ? modification.to.value : v;
            });
        } else {
          const modification = modifications[currentValue];
          if (modification) {
            updatedRow[newColumn.name] = modification.to?.value || currentValue;
          } else {
            updatedRow[newColumn.name] = currentValue;
          }
        }
        updatedRows[index] = updatedRow;
      });
      return updatedRows;
    }

    return rows;
  }

  /**
   * Copies columns to new array. Ignores internal columns.
   */
  prepareColumns(table: BlazeTable | TableInfo): DatasetColumn[] {
    const filteredColumns: DatasetColumn[] = [];
    table.columns.forEach((col) => {
      if (this.schemaService.isColumnVisible(col.name, table.id)) {
        filteredColumns.push({ ...col });
      }
    });
    return filteredColumns;
  }

  removeRestrictedColumns(
    tableId = '',
    row: { [key: string]: any } = {},
    ignoreColumns: string[] = [],
  ): { [key: string]: any } {
    const colsToIgnore = new Set(ignoreColumns);
    const restrictedCols = this.schemaService.getRestrictedColumns(tableId);
    const rowWithoutRestrictedCols: { [key: string]: any } = {};
    for (const col in row) {
      if (Object.prototype.hasOwnProperty.call(row, col)) {
        if (!restrictedCols.has(col) || colsToIgnore.has(col)) {
          rowWithoutRestrictedCols[col] = row[col];
        }
      }
    }
    return rowWithoutRestrictedCols;
  }

  removeRestrictedColumnsExceptId(
    tableId = '',
    row: { [key: string]: any } = {},
  ): { [key: string]: any } {
    const restrictedCols = this.schemaService.getRestrictedColumns(tableId);
    const idCol = this.schemaService.getIdColumnName(tableId);
    const rowWithoutRestrictedCols: { [key: string]: any } = {};
    for (const col in row) {
      if (Object.prototype.hasOwnProperty.call(row, col)) {
        if (!restrictedCols.has(col) || idCol === col) {
          rowWithoutRestrictedCols[col] = row[col];
        }
      }
    }
    return rowWithoutRestrictedCols;
  }

  renameColumnWhenNeeded(
    table: BlazeTable,
    columnName: string,
    newColumnName: string,
  ): Observable<BlazeTable> {
    if (columnName !== newColumnName) {
      return this.tablesApiService
        .postRenameColumn(table.id, {
          columnName,
          newColumnName,
        })
        .pipe(map((btSchema) => convertBlazeTableSchemaToBlazeTable(btSchema)));
    }

    return of(table);
  }

  /**
   * Checks if there are empty values in the column.
   * @returns `true` if there are empty values in the column
   */
  checkIfEmptyValuesInColumn(table: BlazeTable, columnName: string): Observable<boolean> {
    return this.datasetsApiService
      .postGet({
        datasetId: table.id,
        filterQuery: {
          nodeType: QueryNodeType.Logical,
          logicalType: LogicalType.And,
          children: [
            {
              column: columnName,
              displayInfos: [],
              isFormulaValue: false,
              isOptional: false,
              nodeType: QueryNodeType.Comparison,
              operator: ComparisonOperator.IsNull,
              value: undefined,
            },
          ],
        },
        maxResults: 1,
        projectColumns: [columnName],
      })
      .pipe(map((rows) => !!rows.length));
  }

  deleteTableUI(table: BlazeTable, relatedTables: BlazeTable[]): void {
    const relatedTableName = table.isChangeTrackingTable
      ? relatedTables.find((tab) => tab.changeTracking?.datasetId === table.id)?.name
      : 'unknown name';
    const tableName = table.name || 'unknown name';

    const tableIds: string[] = [
      table.id,
      relatedTables.find((tab) => tab.id === table.changeTracking?.datasetId)?.id,
    ].filter((id): id is string => !!id);

    this.tablesApiService
      .getTablesReferences(tableIds)
      .pipe(
        switchMap((references) => {
          const dialogAction = table.isChangeTrackingTable
            ? 'delete-change-tracking-table'
            : 'delete-table';
          const dialogInput = {
            'delete-table': {
              title: 'Delete Table',
              emptyRefsDescription: `Do you want to delete ${tableName} table?`,
              description:
                '<b>This table is used in the following workflows:</b><br> Deleting it will break them.',
              confirmLabel: 'Delete table',
            },
            'delete-change-tracking-table': {
              title: 'Delete Change Tracking Table',
              emptyRefsDescription: `Do you want to delete ${tableName} table?<br><br>Deleting this table will permanently stop all tracking for the '${relatedTableName}' table.`,
              description: `Deleting this table will permanently stop all tracking for the '${relatedTableName}' table.<br><br><b>This change tracking table is used in the following workflows:</b><br> Deleting it will break them.`,
              confirmLabel: 'Delete table',
            },
          }[dialogAction];
          const dialog = this.dialogService.openReferencesDialog(references, dialogInput);
          this.displayReferencesDialog(dialog, table);
          return dialog.afterClosed();
        }),
      )
      .subscribe((res) => {
        const tableId = table?.id;
        if (!res || !tableId) {
          return;
        }

        this.tableCollService
          .delete(tableId)
          .pipe(finalize(() => this.schemaService.datasetIdsToRefresh$.next([tableId])))
          .subscribe();
      });
  }

  deleteColumn(column: DatasetColumn, config: BlazeTable): DatasetColumn | undefined {
    const colIndex = findColumnIndex(config.columns, column);
    if (config.columns.length === 1 || colIndex === -1) {
      return undefined;
    }
    const col = { ...config.columns[colIndex] };
    config.columns = deleteItemFromArray(config.columns, colIndex);

    if (col.name === config.displayColumn) {
      const cols = config.columns.filter((c) => !c.isInline && !c.isInternal);
      if (config.columns.filter((c) => !c.isInline).length) {
        config.displayColumn = cols[0].name;
      }
    }
    return col;
  }

  deleteTableColumnUI(
    column: DatasetColumn,
    config: BlazeTable,
  ): Observable<BlazeTable | undefined> {
    const { id, columnType } = column;
    const dependentColumnIds = column.dependentColumnInfos || [];
    const referencesToCheck: { columnId: ColumnId; tableId: string }[] = [
      { tableId: config.id, columnId: id },
      ...dependentColumnIds.map((col) => ({ tableId: col.datasetId, columnId: col.columnId })),
    ].filter(
      (col): col is { columnId: ColumnId; tableId: string } => !!(col.columnId && col.tableId),
    );
    return this.checkColumnReferences(referencesToCheck, columnType, id, config.id).pipe(
      concatMap((confirmation) => {
        if (confirmation) {
          return of(this.deleteColumn(column, config)).pipe(
            map((column) => {
              this.blazeTableStore.requestRecordsUpdate({ column, config });
              return config;
            }),
          );
        }
        return of(config);
      }),
    );
  }

  checkColumnReferences(
    referencesToCheck: { columnId: ColumnId; tableId: string }[],
    sourceColumnType: ColumnType | undefined,
    sourceColumnId: ColumnId | undefined,
    sourceColumnDatasetId: string | undefined,
  ): Observable<boolean> {
    const dialogAction = sourceColumnType === 'Link' ? 'delete-linked-column' : 'delete-column';

    const dialogInput = {
      'delete-column': {
        title: 'Delete Column',
        emptyRefsDescription: 'Do you want to delete this column?',
        description:
          '<b>This column is used in the following workflows:</b><br> Deleting it will break them.',
        confirmLabel: 'Delete column',
      },
      'delete-linked-column': {
        title: 'Delete Linked Column',
        emptyRefsDescription:
          'Do you want to delete this column?<br><br> All additional linked columns from this table will be removed when the link column is deleted',
        description:
          '<b>This column is used in the following workflows:</b><br> Deleting it will break them.',
        confirmLabel: 'Delete column',
      },
    }[dialogAction];

    return this.tablesApiService.getColumnsReferences(referencesToCheck).pipe(
      switchMap((references) => {
        const dialog = this.dialogService.openReferencesDialog(references, dialogInput);
        dialog.componentInstance.targetEmitter
          .pipe(untilDestroyed(dialog.componentInstance), take(1))
          .subscribe((reference) => {
            if (sourceColumnType && sourceColumnId && sourceColumnDatasetId) {
              this.store.dispatch(
                fromTargetWorkflow.setTargetWorkflow({
                  workflow: reference,
                  action: {
                    type: 'delete-column',
                    tableId: sourceColumnDatasetId,
                    columnId: sourceColumnId,
                  },
                }),
              );
              dialog.close();
            }
          });
        return dialog.afterClosed();
      }),
    );
  }

  private displayReferencesDialog(
    dialog: MatDialogRef<{ targetEmitter: Observable<ReferenceInfo> }>,
    table: BlazeTable,
  ): void {
    dialog.componentInstance.targetEmitter
      .pipe(untilDestroyed(dialog.componentInstance), take(1))
      .subscribe((reference) => {
        this.store.dispatch(
          fromTargetWorkflow.setTargetWorkflow({
            workflow: reference,
            action: {
              type: 'delete-table',
              tableId: table.id,
            },
          }),
        );
        dialog.close();
      });
  }
}
